import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import ReactSwitch from "react-switch";
import { parse } from "query-string";
import useProyecto from "../hooks/useProyecto";
import { ModalContext } from "../context/ModalContext";
import { EntradasContext } from "../context/EntradasContext";
import { ReportesContext } from "../context/ReportesContext";
import ReporteForm from "../components/reportes/ReporteForm";
import { ProyectosContext } from "../context/ProyectosContext";
import { PartidasContext } from "../context/PartidasContext";
import ReporteHeader from "../components/reportes/ReporteHeader";
import ReporteGantt from "../components/reportes/ReporteGantt";
import ReporteContenido from "../components/reportes/ReporteContenido";
import ReporteAvanceBars from "../components/reportes/ReporteAvanceBars";
import ReporteGanttPartidas from "../components/reportes/ReporteGanttPartidas";
import ReporteIndicadores from "../components/reportes/ReporteIndicadores";

const OpcionSwitch = ({ label, checked, disabled, onChange }) => (
  <div className="d-flex align-items-center mt-3">
    <ReactSwitch checked={checked} disabled={disabled} onChange={onChange} />
    <label className="mb-0 ms-2">{label}</label>
  </div>
);

const ReportPreview = () => {
  const [startDate, setStartDate] = useState(
    moment().startOf("isoWeek").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("week").format("YYYY-MM-DD"),
  );

  const [fetching, setFetching] = useState(false);
  const [incluirGantt, setIncluirGantt] = useState(false);
  const [incluirFinanciero, setIncluirFinanciero] = useState(false);
  const [incluirFechaTerminacion, setIncluirFechaTerminacion] =
    useState(false);
  // idAdjunto of the images the user unchecked; everything else is included.
  const [excluidos, setExcluidos] = useState(new Set());

  const { start_date, end_date } = parse(window.location.search);
  const { proyecto, idProyecto } = useProyecto();

  const {
    resumen,
    resumen_partidas,
    entradas,
    getEntradas,
    resetEntradas,
    getEntradasResumen,
  } =
    useContext(EntradasContext);
  const { modalComponent, clearModal } = useContext(ModalContext);
  const { getProgresoObra, progresoObra, avanceFinanciero } =
    useContext(ProyectosContext);
  const { setReporte } = useContext(ReportesContext);
  const { partidas, getPartidasProyecto } = useContext(PartidasContext);

  const getLastNumber = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    for (let i = arr.length - 1; i >= 0; i--) {
      const n = parseFloat(arr[i]);
      if (!isNaN(n)) return n;
    }
    return null;
  };

  const programado = getLastNumber(progresoObra?.programadoSemana);
  const real = getLastNumber(progresoObra?.realSemana);

  // avanceFinanciero is loaded by ReporteIndicadores as of endDate.
  const financieroReporte = incluirFinanciero ? avanceFinanciero : null;

  const reporte = {
    resumen,
    entradas,
    programado,
    real,
    fecha_fin: endDate,
    fecha_inicio: startDate,
    semanaActual: proyecto?.semanaActual,
  };

  const getAdjuntosReporte = () =>
    (Array.isArray(entradas) ? entradas : [])
      .map(({ Adjunto }) => Adjunto)
      .filter(Boolean)
      .map(({ idAdjunto }) => idAdjunto);

  const handleToggleAdjunto = (idAdjunto) => {
    setExcluidos((prev) => {
      const next = new Set(prev);
      if (next.has(idAdjunto)) next.delete(idAdjunto);
      else next.add(idAdjunto);
      return next;
    });
  };

  const handleSetAllAdjuntos = (incluir) => {
    setExcluidos(incluir ? new Set() : new Set(getAdjuntosReporte()));
  };

  const handleSave = () => {
    setReporte({
      adjuntos: getAdjuntosReporte().filter((id) => !excluidos.has(id)),
      resumen,
      resumen_partidas,
      incluir_financiero: incluirFinanciero,
      incluir_fecha_terminacion: incluirFechaTerminacion,
      idProyecto,
      programado,
      real,
      fecha_fin: endDate,
      fecha_inicio: startDate,
    });
    modalComponent(
      "Guardar Reporte",
      <ReporteForm handleCancel={clearModal} />,
    );
  };

  const fetchEntradas = () => {
    if (!idProyecto || !startDate || !endDate) return;
    resetEntradas();
    // Only bound the series by end_date so it runs from the project start to
    // end_date; its last cumulative point then matches the proyecto endpoint's
    // `real`/`programado` for that date.
    getProgresoObra(idProyecto, {
      end_date: endDate,
    });
    getEntradas(idProyecto, null, null, {
      end_date: endDate,
      start_date: startDate,
    });
  };

  const generarResumen = () => {
    if (!idProyecto || !startDate || !endDate) return;
    setFetching(true);
    getEntradasResumen(idProyecto, null, null, {
      end_date: endDate,
      start_date: startDate,
    });
  };

  // Regenerating replaces the current AI summary, so ask first when one exists.
  const handleGenerar = () => {
    if (!resumen) return generarResumen();
    modalComponent(
      "¿Regenerar Resumen?",
      <div>
        <p>
          Este reporte ya tiene un resumen. Si lo generas de nuevo, el resumen
          actual será reemplazado y no podrás recuperarlo.
        </p>
        <div className="row">
          <div className="col-6">
            <button
              type="button"
              onClick={clearModal}
              className="btn w-100 text-muted"
            >
              Cancelar
            </button>
          </div>
          <div className="col-6">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={() => {
                clearModal();
                generarResumen();
              }}
            >
              Regenerar
            </button>
          </div>
        </div>
      </div>,
    );
  };

  useEffect(() => {
    // New period, new set of images: start with all of them selected.
    setExcluidos(new Set());
    fetchEntradas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProyecto, startDate, endDate]);

  useEffect(() => {
    if (idProyecto && incluirGantt) getPartidasProyecto(idProyecto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProyecto, incluirGantt]);

  useEffect(() => {
    if (resumen && resumen !== null) {
      setFetching(false);
    }
  }, [resumen]);

  useEffect(() => {
    if (
      start_date &&
      start_date !== "" &&
      start_date !== null &&
      start_date !== startDate
    ) {
      setStartDate(start_date);
    }
    if (
      end_date &&
      end_date !== "" &&
      end_date !== null &&
      end_date !== endDate
    ) {
      setEndDate(end_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProyecto, start_date, end_date]);

  return (
    <div className="container-fluid pb-100">
      <div id="report-header" className="row mb-4">
        <div className="col-12 col-md-6">
          <h1 className="h5 my-2">Vista Previa de Reporte</h1>
          {resumen && resumen !== null && (
            <button
              className="btn btn-primary"
              disabled={fetching}
              onClick={handleSave}
            >
              <i className="fa fa-save me-1" /> Guardar
            </button>
          )}
        </div>
        <div className="col-12 col-md-6">
          <div className="card shadow p-3">
            <div className="row">
              <div className="col-6">
                <label>Desde</label>
                <input
                  type="date"
                  value={startDate}
                  disabled={fetching}
                  className="form-control"
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-6">
                <label>Hasta</label>
                <input
                  type="date"
                  value={endDate}
                  disabled={fetching}
                  className="form-control"
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <OpcionSwitch
              label="Incluir Gantt de Partidas"
              checked={incluirGantt}
              disabled={fetching}
              onChange={setIncluirGantt}
            />
            <OpcionSwitch
              label="Incluir Información Financiera"
              checked={incluirFinanciero}
              disabled={fetching}
              onChange={setIncluirFinanciero}
            />
            <OpcionSwitch
              label="Incluir Fecha Prevista de Terminación"
              checked={incluirFechaTerminacion}
              disabled={fetching}
              onChange={setIncluirFechaTerminacion}
            />
            <button
              onClick={handleGenerar}
              disabled={fetching}
              className="btn bold btn-sm mt-3 btn-primary"
            >
              {fetching ? "Generando..." : "Generar Reporte"}
            </button>
          </div>
        </div>
      </div>
      <div className="card shadow p-3">
        <div className="row align-items-center">
          <div className="col-12 col-md-6">
            <ReporteAvanceBars
              programado={programado}
              real={real}
              financiero={financieroReporte}
            />
          </div>
          <div className="col-12 col-md-6">
            <ReporteHeader reporte={reporte} />
          </div>
        </div>
        <ReporteGantt reporte={reporte} />
        <ReporteContenido
          seccion="resumen"
          reporte={reporte}
          fetching={fetching}
          onGenerar={handleGenerar}
        />
        <ReporteIndicadores
          idProyecto={idProyecto}
          fecha={endDate}
          incluirFinanciero={incluirFinanciero}
          incluirFechaTerminacion={incluirFechaTerminacion}
        />
        {incluirGantt &&
          Array.isArray(partidas) &&
          partidas.length > 0 && (
            <div className="mt-3 mb-4">
              <ReporteGanttPartidas
                partidas={partidas.filter((p) => Number(p.programado) > 0)}
                resumenes={resumen_partidas}
              />
            </div>
          )}
        <ReporteContenido
          seccion="imagenes"
          reporte={reporte}
          fetching={fetching}
          onGenerar={handleGenerar}
          excluidos={excluidos}
          onToggleAdjunto={handleToggleAdjunto}
          onSetAllAdjuntos={handleSetAllAdjuntos}
        />
        {resumen && resumen !== null && (
          <div className="text-end mt-3">
            <button
              className="btn btn-primary"
              disabled={fetching}
              onClick={handleSave}
            >
              <i className="fa fa-save me-1" /> Guardar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPreview;
